import {array, boolean, constant, Decoder, dict, fail, number, object, oneOf, string, succeed, union} from "@mojotech/json-type-validation";
import {guidDecoder} from "../utils";
import {AlertLevel, AlertMessage, ClientRiskData, ClientRiskMetric, LimitType, LoggedIn, RawDashboardResponse, RJEConnectionStatus, Severity, Unknown} from "../types/types";
import {HasCode} from "../types/types";
import {ClientConfig} from "../types/clients";
import {isValid, parse, parseISO} from "date-fns";
import {ClientVirtualServerSuspensionInfo} from "../types/suspensions";


const severityDecoder = oneOf<Severity>(
  constant<'Notify'>('Notify'),
  constant<'Warn'>('Warn'),
  constant<'Breach'>('Breach'),
)

// Is there a less repetitive way to write decoders for string unions?
export const alertLevelDecoder = oneOf<AlertLevel>(
  constant<'Baseline'>('Baseline'),
  severityDecoder,
)

const iso8601DateStringDecoder = string().andThen(function tryParseDate(
  timestamp: string,
): Decoder<Date> {
  const parsedDate = parse(timestamp, "yyyy-MM-dd'T'HH:mm:ssXXX", 0)
  if (isValid(parsedDate)) {
    return succeed(parsedDate)
  }
  const parsedDateWithMillis = parse(
    timestamp,
    "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
    0,
  )
  if (isValid(parsedDateWithMillis)) {
    return succeed(parsedDateWithMillis)
  }
  const parsedAsISO8601 = parseISO(timestamp)
  if (isValid(parsedDateWithMillis)) {
    return succeed(parsedDateWithMillis)
  }
  if (isValid(parsedAsISO8601)) {
    return succeed(parsedAsISO8601)
  }
  return fail(
    `Not a valid date string: "${timestamp}". Should look like one of: 
    "2018-12-31T01:23:45+00:00", "2018-12-31T01:23:45.678+00:00", "2019-02-11T06:28:22.5075397Z".`,
  )
})

// from https://github.com/mojotech/json-type-validation/issues/37
// the json-type-validation library provides support for undefined/optional values
// HOWEVER, we get null from the server, which is completely unsupported
// what a great library
const nullToUndefined = function <T>(decoder: Decoder<T>): Decoder<T | undefined> {
  return union(decoder, constant(null)).map((val) => val === null ? undefined : val);
}

const unknownIfNull = function <T>(decoder: Decoder<string>): Decoder<string | Unknown> {
  return union(decoder, constant(null)).map((val) => val === null ? "Unknown" : val);
}


const limitTypeDecoder = oneOf(
  constant<LimitType>('RC1_total_clip_size'),
  constant<LimitType>('RC2_exposure'),
  constant<LimitType>('RC6_unactioned_orders'),
  constant<LimitType>('RC7_messages_per_second'),
  constant<LimitType>('Virtual_server_suspension'),
  constant<LimitType>('Realistic_exposure_value'),
  constant<LimitType>('Historic_Max_Realistic_exposure'),
)

export const alertMessageDecoder = object<AlertMessage>({
  alertSeverity: severityDecoder,
  alertTimestamp: iso8601DateStringDecoder,
  clientId: guidDecoder,
  companyCode: unknownIfNull(string()),
  companyName: unknownIfNull(string()),
  exceeded: boolean(),
  limitType: limitTypeDecoder,
  percentValue: nullToUndefined(number()),
  rjeMessageNumber: number(),
  rjeMessageTimestamp: iso8601DateStringDecoder,
  sequenceNumber: number(),
  thresholdPercent: nullToUndefined(number()),
  value: nullToUndefined(number()),
  virtualServerNumber: string()
})

export const userDecoder: Decoder<LoggedIn> = object({
  hasBusinessAdminRole: boolean(),
  hasITAdminRole: boolean(),
  hasRiskUserRole: boolean(),
  hasTVModeRole: boolean(),
  type: succeed<'LoggedIn'>('LoggedIn'),
  username: string(),
})

const clientDecoder: Decoder<ClientConfig> = object({
  alerts: array(
    object({
      contacts: array(
        object({
          emailAddress: string(),
          id: guidDecoder,
        }),
      ).andThen((arrayResult) => succeed(new Set(arrayResult))),
      id: guidDecoder,
      limitType: limitTypeDecoder,
      thresholdInPercent: number(),
    }),
  ),
  code: string(),
  isActive: boolean(),
  exposureLimitInJpy: number(),
  id: guidDecoder,
  originalId: guidDecoder,
  name: string(),
  tcsLimitInJPY: number(),
  cumulativeOrderValueLimitInJPY: number(),
  cumulativeOrderValueTimeIntervalSeconds: number(),
  cumulativeExecutionValueLimitInJPY: nullToUndefined(number()),
  cumulativeExecutionValueTimeIntervalSeconds: nullToUndefined(number()),
  unactionedOrdersLimit: number(),
  virtualServerGroupId: string(),
  virtualServers: array(
    object({
      id: guidDecoder,
      serverId: string(),
    }),
  ).andThen((servers) => succeed(new Set(servers))),
  realisticExposureLimit: number(),
})

export const clientArrayDecoder: Decoder<ReadonlyArray<ClientConfig>> = array(clientDecoder)

export const toastResponseDecoder: Decoder<ReadonlyArray<{ readonly title: string; readonly message: string }>> = array(
  object({
    title: string(),
    message: string(),
  }),
)

export const hasCodeDecoder: Decoder<HasCode> = object({code: string()})

const clientRiskMetricDecoder = object<ClientRiskMetric>({
  breachLimit: number(),
  percentValue: number(),
  value: number(),
})

const clientRiskDataDecoder = object<ClientRiskData>({
  rc2: clientRiskMetricDecoder,
  rc6: clientRiskMetricDecoder,
  rev: clientRiskMetricDecoder,
  maxRev: clientRiskMetricDecoder,
  historicMaxRev: clientRiskMetricDecoder
})

export const rawDashboardResponseDecoder = object<RawDashboardResponse>({
  data: dict(clientRiskDataDecoder),
  latestMessageTimestamp: iso8601DateStringDecoder,
  totalMessageCount: number(),
  dailyTotalNewOrderValue: number(),
  dailyExecutedOrderValue: number(),
})

export const decodeRJEConnectionStatus = oneOf<RJEConnectionStatus>(
  constant<'Connected'>('Connected'),
  constant<'Connecting'>('Connecting'),
  constant<'Retrying'>('Retrying'),
  constant<'ParseError'>('ParseError'),
)

export const orderNoticeAdminResponseDecoder: Decoder<{
  readonly success: boolean
  readonly message: string
}> = object({
  message: string(),
  success: boolean(),
})

const suspensionDecoder: Decoder<ClientVirtualServerSuspensionInfo> = object({
  code: unknownIfNull(string()),
  virtualServerIds: array(string())
})

export const suspensionArrayDecoder: Decoder<ReadonlyArray<ClientVirtualServerSuspensionInfo>> = array(suspensionDecoder)
